import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { IoService } from '../io/io.service';
import { ReportService } from '../report/report.service';

@Injectable({
  providedIn: 'root'
})
export class ListBuilderService {

  private listBuilderActive = false;
  private activeList: any = null;
  private members: any[] = [];
  private lists: any[] = [];
  private activeReportKey: string = null;
  private activeViewKey: string = null;
  private activeViewRoute: string = null;
  private activeItemType: string = null;
  private activeListActions: any[] = [];
  private activeFields: any[] = [];
  private memberIdSet = new Set<string>();

  private listBuilderActiveChanged = new Subject<boolean>();
  private listUpdated = new Subject<any>();
  private listsLoaded = new Subject<any[]>();
  private activeFieldsChanged = new Subject<any[]>();

  constructor(
    private ioService: IoService,
    private reportService: ReportService,
  ) { }

  // The current report's clientConfig (filters/search/location) — needed so the
  // backend can compute member columns for custom/specialty reports.
  private currentClientConfig(): any {
    try {
      return this.reportService.getLoadRecordsInput()?.clientConfig || {};
    } catch (e) {
      return {};
    }
  }

  // ─── Observables ─────────────────────────────────────────────

  listBuilderActiveChangedObservable(): Observable<boolean> {
    return this.listBuilderActiveChanged.asObservable();
  }

  listUpdatedObservable(): Observable<any> {
    return this.listUpdated.asObservable();
  }

  listsLoadedObservable(): Observable<any[]> {
    return this.listsLoaded.asObservable();
  }

  activeFieldsChangedObservable(): Observable<any[]> {
    return this.activeFieldsChanged.asObservable();
  }

  // ─── Getters ─────────────────────────────────────────────────

  isActive(): boolean {
    return this.listBuilderActive;
  }

  getActiveList(): any {
    return this.activeList;
  }

  getMembers(): any[] {
    return this.members;
  }

  getLists(): any[] {
    return this.lists;
  }

  getActiveFields(): any[] {
    return this.activeFields;
  }

  getActiveReportKey(): string {
    return this.activeReportKey;
  }

  getActiveViewKey(): string {
    return this.activeViewKey;
  }

  setActiveViewKey(viewKey: string) {
    this.activeViewKey = viewKey;
  }

  getActiveViewRoute(): string {
    return this.activeViewRoute;
  }

  setActiveViewRoute(viewRoute: string) {
    this.activeViewRoute = viewRoute;
  }

  getActiveItemType(): string {
    return this.activeItemType;
  }

  setActiveItemType(itemType: string) {
    this.activeItemType = itemType;
  }

  getActiveListActions(): any[] {
    return this.activeListActions || [];
  }

  setActiveListActions(listActions: any[]) {
    this.activeListActions = listActions || [];
  }

  isItemInList(itemId: string): boolean {
    return this.memberIdSet.has(itemId);
  }

  // ─── Panel Control ───────────────────────────────────────────

  async openListBuilder(reportKey: string) {
    this.activeReportKey = reportKey;
    this.listBuilderActive = true;
    this.listBuilderActiveChanged.next(true);

    try {
      const result: any = await this.ioService.post('/list/getLists', { reportKey });
      this.lists = result.lists || [];
      this.listsLoaded.next(this.lists);
    } catch (e) {
      console.error('Error loading lists:', e);
      this.lists = [];
      this.listsLoaded.next(this.lists);
    }
  }

  closeListBuilder() {
    this.listBuilderActive = false;
    this.activeList = null;
    this.members = [];
    this.memberIdSet.clear();
    this.listBuilderActiveChanged.next(false);
  }

  setActiveFields(fields: any[]) {
    // Calculated fields are computed per-document by the report server and don't exist
    // on list members, so exclude them from the list view's columns.
    this.activeFields = (fields || []).filter(f => f && f.calculated !== true);
    this.activeFieldsChanged.next(this.activeFields);
  }

  // ─── List Operations ─────────────────────────────────────────

  async createList(name: string, reportKey: string, locationId: string) {
    try {
      const result: any = await this.ioService.post('/list/createList', {
        reportKey,
        name,
        locationId,
      });
      this.activeList = result.list;
      this.members = [];
      this.rebuildMemberIdSet();
      this.listUpdated.next({ list: this.activeList, members: this.members });

      this.lists.unshift({
        _id: result.list._id,
        key: result.list.key,
        name: result.list.name,
        memberCount: 0,
      });
      this.listsLoaded.next(this.lists);
    } catch (e) {
      console.error('Error creating list:', e);
    }
  }

  async loadList(listKey: string) {
    try {
      const activeFieldPaths = this.activeFields.map(f => f.path);
      const result: any = await this.ioService.post('/list/getList', {
        listKey,
        activeFields: activeFieldPaths,
        clientConfig: this.currentClientConfig(),
        utcOffset: new Date().getTimezoneOffset(),
      });
      this.activeList = result.list;
      this.members = result.members || [];
      this.rebuildMemberIdSet();
      this.listUpdated.next({ list: this.activeList, members: this.members });
    } catch (e) {
      console.error('Error loading list:', e);
    }
  }

  // ─── Item Operations ─────────────────────────────────────────

  async addItem(itemId: string) {
    if (!this.activeList) return;
    if (this.memberIdSet.has(itemId)) return;

    // Optimistic: add placeholder immediately
    const placeholder = { _id: itemId, _loading: true };
    this.members.push(placeholder);
    this.memberIdSet.add(itemId);
    this.listUpdated.next({ list: this.activeList, members: [...this.members] });

    try {
      const activeFieldPaths = this.activeFields.map(f => f.path);
      const result: any = await this.ioService.post('/list/addItem', {
        listKey: this.activeList.key,
        itemId,
        activeFields: activeFieldPaths,
        clientConfig: this.currentClientConfig(),
        utcOffset: new Date().getTimezoneOffset(),
      });

      this.activeList = result.list;
      if (result.member) {
        const idx = this.members.findIndex(m => m._id === itemId);
        if (idx !== -1) {
          this.members[idx] = result.member;
        }
      }
      this.rebuildMemberIdSet();
      this.listUpdated.next({ list: this.activeList, members: [...this.members] });
    } catch (e) {
      this.members = this.members.filter(m => m._id !== itemId);
      this.memberIdSet.delete(itemId);
      this.listUpdated.next({ list: this.activeList, members: [...this.members] });
      console.error('Error adding item:', e);
    }
  }

  async removeItem(itemId: string) {
    if (!this.activeList) return;

    const removedIndex = this.members.findIndex(m => (m._id?.toString?.() || m._id) === itemId);
    const removedItem = removedIndex !== -1 ? this.members[removedIndex] : null;
    this.members = this.members.filter(m => (m._id?.toString?.() || m._id) !== itemId);
    this.memberIdSet.delete(itemId);
    this.listUpdated.next({ list: this.activeList, members: [...this.members] });

    try {
      const result: any = await this.ioService.post('/list/removeItem', {
        listKey: this.activeList.key,
        itemId,
      });
      this.activeList = result.list;
      this.rebuildMemberIdSet();
    } catch (e) {
      if (removedItem && removedIndex !== -1) {
        this.members.splice(removedIndex, 0, removedItem);
        this.memberIdSet.add(itemId);
        this.listUpdated.next({ list: this.activeList, members: [...this.members] });
      }
      console.error('Error removing item:', e);
    }
  }

  // ─── Bulk Operations ─────────────────────────────────────────

  async addAll(clientConfig: any, utcOffset: number) {
    if (!this.activeList) return;

    try {
      const activeFieldPaths = this.activeFields.map(f => f.path);
      const result: any = await this.ioService.post('/list/addAll', {
        listKey: this.activeList.key,
        reportKey: this.activeReportKey,
        clientConfig,
        utcOffset,
        activeFields: activeFieldPaths,
      });
      this.activeList = result.list;
      this.members = result.members || [];
      this.rebuildMemberIdSet();
      this.listUpdated.next({ list: this.activeList, members: [...this.members] });
    } catch (e) {
      console.error('Error adding all:', e);
    }
  }

  async removeAll(clientConfig: any, utcOffset: number) {
    if (!this.activeList) return;

    try {
      const activeFieldPaths = this.activeFields.map(f => f.path);
      const result: any = await this.ioService.post('/list/removeAll', {
        listKey: this.activeList.key,
        reportKey: this.activeReportKey,
        clientConfig,
        utcOffset,
        activeFields: activeFieldPaths,
      });
      this.activeList = result.list;
      this.members = result.members || [];
      this.rebuildMemberIdSet();
      this.listUpdated.next({ list: this.activeList, members: [...this.members] });
    } catch (e) {
      console.error('Error removing all:', e);
    }
  }

  // ─── List Management ─────────────────────────────────────────

  async renameList(name: string) {
    if (!this.activeList) return;

    try {
      const result: any = await this.ioService.post('/list/renameList', {
        listKey: this.activeList.key,
        name,
      });
      this.activeList = result.list;

      const listEntry = this.lists.find(l => l.key === this.activeList.key);
      if (listEntry) listEntry.name = name;
      this.listsLoaded.next(this.lists);
      this.listUpdated.next({ list: this.activeList, members: this.members });
    } catch (e) {
      console.error('Error renaming list:', e);
    }
  }

  async deleteList() {
    if (!this.activeList) return;

    const deletedKey = this.activeList.key;

    try {
      await this.ioService.post('/list/deleteList', {
        listKey: deletedKey,
      });
      this.activeList = null;
      this.members = [];
      this.memberIdSet.clear();
      this.lists = this.lists.filter(l => l.key !== deletedKey);
      this.listsLoaded.next(this.lists);
      this.listUpdated.next({ list: null, members: [] });
    } catch (e) {
      console.error('Error deleting list:', e);
    }
  }

  // ─── Internal ────────────────────────────────────────────────

  private rebuildMemberIdSet() {
    this.memberIdSet.clear();
    if (this.activeList?.memberIds) {
      for (const id of this.activeList.memberIds) {
        this.memberIdSet.add(id.toString?.() || id);
      }
    }
    this.syncListDropdownCount();
  }

  private syncListDropdownCount() {
    if (!this.activeList) return;
    const entry = this.lists.find(l => l.key === this.activeList.key);
    if (entry) {
      entry.memberCount = this.activeList.memberIds?.length || 0;
      this.listsLoaded.next(this.lists);
    }
  }
}
